import HCaptcha from "@hcaptcha/react-hcaptcha";
import {
	IconBrandGithub,
	IconBrandLinkedin,
	IconBrandX,
} from "@tabler/icons-react";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { useContext, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { ThemeContext } from "@/components/providers/theme-provider";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { getPublicRuntimeConfigFn } from "@/server/public-config";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

const contactSchema = z.object({
	firstName: z.string().max(120).optional(),
	lastName: z.string().max(120).optional(),
	email: z.string().email("Please enter a valid email address").max(320),
	subject: z
		.string()
		.min(1, "Subject is required")
		.max(500, "Subject is too long"),
	message: z
		.string()
		.min(1, "Message is required")
		.max(50_000, "Message is too long"),
	captchaToken: z.string().min(1, "Captcha is required"),
});

type ContactForm = z.infer<typeof contactSchema>;

const fieldClass =
	"rounded-xl border-border/80 bg-muted/20 transition-colors hover:bg-muted/35 focus-visible:border-primary/40 dark:bg-muted/15";

function FieldError({
	touched,
	errors,
}: {
	touched: boolean;
	errors: unknown[];
}) {
	if (!touched || errors.length === 0) return null;
	const err = errors[0];
	const message =
		typeof err === "string"
			? err
			: err &&
					typeof err === "object" &&
					"message" in err &&
					typeof (err as { message: unknown }).message === "string"
				? (err as { message: string }).message
				: "Invalid input";
	return <p className="text-sm text-destructive">{message}</p>;
}

function formatApiErrorBody(data: unknown): string {
	if (!data || typeof data !== "object") {
		return "Something went wrong. Please try again.";
	}
	const o = data as Record<string, unknown>;
	const err = o.error;
	if (typeof err !== "string") {
		return "Something went wrong. Please try again.";
	}
	const details = o.details;
	if (typeof details === "string" && details.length > 0) {
		return `${err}: ${details}`;
	}
	return err;
}

/** Public handle from a profile URL (e.g. `/in/name` on LinkedIn, last path segment elsewhere). */
function socialHandleFromUrl(href: string): string {
	try {
		const url = new URL(href);
		const parts = url.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
		if (
			url.hostname.includes("linkedin.com") &&
			parts[0] === "in" &&
			parts[1]
		) {
			return parts[1];
		}
		const last = parts[parts.length - 1];
		if (last) return last;
	} catch {
		/* invalid URL */
	}
	return href;
}

const socialLinkDefs = [
	{
		id: "Twitter" as const,
		href: config.Socials.Twitter ?? "",
		icon: IconBrandX,
	},
	{
		id: "Github" as const,
		href: config.Socials.Github ?? "",
		icon: IconBrandGithub,
	},
	{
		id: "Linkedin" as const,
		href: config.Socials.Linkedin ?? "",
		icon: IconBrandLinkedin,
	},
];

const socialLinks = socialLinkDefs
	.filter((l) => l.href.length > 0)
	.map((l) => ({
		...l,
		handle: socialHandleFromUrl(l.href),
	}));

export const Route = createFileRoute("/_pages/contact")({
	component: RouteComponent,
	loader: async () => getPublicRuntimeConfigFn(),
	head: () => {
		const base = seo({
			title: `Contact | ${config.Name}`,
			description: `Get in touch with ${config.Name} — whether it's about a project, a question, or just to say hello.`,
			url: `${config.Url}/contact`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(schema.contactPage()),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Contact", url: `${config.Url}/contact` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const { hcaptchaSiteKey } = Route.useLoaderData();
	const themeCtx = useContext(ThemeContext);
	const hcaptchaTheme: "dark" | "light" =
		themeCtx?.darkTheme === false ? "light" : "dark";

	const captchaRef = useRef<InstanceType<typeof HCaptcha> | null>(null);
	const [captchaToken, setCaptchaToken] = useState<string | null>(null);

	const resetCaptcha = () => {
		setCaptchaToken(null);
		captchaRef.current?.resetCaptcha();
	};

	const form = useForm({
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			subject: "",
			message: "",
			captchaToken: "",
		} as ContactForm,
		onSubmit: async ({ value }) => {
			if (!captchaToken) {
				toast.error("Please complete the captcha");
				return;
			}

			try {
				const response = await fetch("/api/contact", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						...value,
						captchaToken,
					}),
				});

				let body: unknown;
				try {
					body = await response.json();
				} catch {
					body = null;
				}

				if (!response.ok) {
					const message = formatApiErrorBody(body);
					toast.error(message);
					resetCaptcha();
					return;
				}

				form.reset();
				resetCaptcha();
				toast.success("Message sent. I will get back to you as soon as I can.");
			} catch (error) {
				console.error("Error submitting form:", error);
				toast.error("Could not reach the server. Please try again.");
				resetCaptcha();
			}
		},
	});

	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-50"
				aria-hidden
			/>

			<main className="relative pb-20 pt-12 sm:pt-20">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Contact"
							description="Tell me about a project, a question, or just say hello. I read every message."
						/>
					</div>

					<div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
						<aside className="w-full shrink-0 animate-in fade-in slide-in-from-bottom-8 duration-800 delay-200 fill-mode-both lg:sticky lg:top-24 lg:w-80">
							<Card className="border-border/80 bg-card/90 shadow-lg backdrop-blur-sm dark:bg-card dark:shadow-none">
								<CardHeader className="pb-4">
									<CardTitle className="text-lg font-semibold tracking-tight">
										Other ways to reach me
									</CardTitle>
									<CardDescription className="text-pretty">
										Direct links on the platforms I use.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-5">
									{socialLinks.length > 0 ? (
										<ul className="flex flex-col gap-2">
											{socialLinks.map(({ id, href, handle, icon: Icon }) => (
												<li key={id}>
													<a
														href={href}
														target="_blank"
														rel="noopener noreferrer"
														aria-label={`${id} profile`}
														className="flex items-center gap-2.5 rounded-xl border border-border/80 bg-muted/10 px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-muted/25"
													>
														<Icon
															className="size-4 shrink-0 text-muted-foreground"
															aria-hidden
														/>
														<span className="min-w-0 truncate font-medium">
															<span className="text-muted-foreground">/</span>
															{handle}
														</span>
													</a>
												</li>
											))}
										</ul>
									) : null}
								</CardContent>
							</Card>
						</aside>

						<div className="min-w-0 flex-1 animate-in fade-in slide-in-from-bottom-8 duration-800 delay-300 fill-mode-both">
							<div className="relative">
								<div
									className="pointer-events-none absolute -inset-px rounded-2xl bg-linear-to-br from-primary/15 to-accent/10 opacity-60 blur-xl dark:hidden"
									aria-hidden
								/>
								<Card className="relative rounded-2xl border-border/80 bg-card/90 p-6 shadow-lg backdrop-blur-sm dark:bg-card dark:shadow-none sm:p-8 lg:p-10">
									<form
										onSubmit={(e) => {
											e.preventDefault();
											e.stopPropagation();
											form.handleSubmit();
										}}
										className="space-y-6"
									>
										<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
											<form.Field
												name="firstName"
												validators={{
													onChange: contactSchema.shape.firstName,
												}}
											>
												{(field) => (
													<div className="space-y-2">
														<Label
															htmlFor={field.name}
															className="font-medium text-foreground"
														>
															First name
														</Label>
														<Input
															id={field.name}
															name={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(e) =>
																field.handleChange(e.target.value)
															}
															placeholder="Optional"
															autoComplete="given-name"
															className={fieldClass}
														/>
														<FieldError
															touched={field.state.meta.isTouched}
															errors={field.state.meta.errors}
														/>
													</div>
												)}
											</form.Field>

											<form.Field
												name="lastName"
												validators={{
													onChange: contactSchema.shape.lastName,
												}}
											>
												{(field) => (
													<div className="space-y-2">
														<Label
															htmlFor={field.name}
															className="font-medium text-foreground"
														>
															Last name
														</Label>
														<Input
															id={field.name}
															name={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(e) =>
																field.handleChange(e.target.value)
															}
															placeholder="Optional"
															autoComplete="family-name"
															className={fieldClass}
														/>
														<FieldError
															touched={field.state.meta.isTouched}
															errors={field.state.meta.errors}
														/>
													</div>
												)}
											</form.Field>
										</div>

										<form.Field
											name="email"
											validators={{
												onChange: contactSchema.shape.email,
											}}
										>
											{(field) => (
												<div className="space-y-2">
													<Label
														htmlFor={field.name}
														className="font-medium text-foreground"
													>
														Email *
													</Label>
													<Input
														id={field.name}
														name={field.name}
														type="email"
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="you@example.com"
														required
														autoComplete="email"
														className={fieldClass}
													/>
													<FieldError
														touched={field.state.meta.isTouched}
														errors={field.state.meta.errors}
													/>
												</div>
											)}
										</form.Field>

										<form.Field
											name="subject"
											validators={{
												onChange: contactSchema.shape.subject,
											}}
										>
											{(field) => (
												<div className="space-y-2">
													<Label
														htmlFor={field.name}
														className="font-medium text-foreground"
													>
														Subject *
													</Label>
													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="What is this about?"
														required
														autoComplete="off"
														className={fieldClass}
													/>
													<FieldError
														touched={field.state.meta.isTouched}
														errors={field.state.meta.errors}
													/>
												</div>
											)}
										</form.Field>

										<form.Field
											name="message"
											validators={{
												onChange: contactSchema.shape.message,
											}}
										>
											{(field) => (
												<div className="space-y-2">
													<Label
														htmlFor={field.name}
														className="font-medium text-foreground"
													>
														Message *
													</Label>
													<Textarea
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Your message"
														rows={8}
														required
														className={cn(fieldClass, "resize-y min-h-[10rem]")}
													/>
													<FieldError
														touched={field.state.meta.isTouched}
														errors={field.state.meta.errors}
													/>
												</div>
											)}
										</form.Field>

										<div className="space-y-2">
											<Label className="font-medium text-foreground">
												Captcha *
											</Label>
											<div className="flex justify-center rounded-xl border border-border/60 bg-muted/10 p-4 dark:bg-muted/5">
												{hcaptchaSiteKey.length > 0 ? (
													<HCaptcha
														ref={captchaRef}
														theme={hcaptchaTheme}
														sitekey={hcaptchaSiteKey}
														onVerify={(token) => setCaptchaToken(token)}
														onExpire={() => setCaptchaToken(null)}
														onError={() => setCaptchaToken(null)}
													/>
												) : (
													<p className="text-sm text-muted-foreground">
														Captcha is unavailable. Set{" "}
														<code className="rounded bg-muted px-1 py-0.5 text-xs">
															HCAPTCHA_SITE_KEY
														</code>{" "}
														as a plaintext Worker variable (not a secret).
													</p>
												)}
											</div>
										</div>

										<form.Subscribe
											selector={(state) => [
												state.canSubmit,
												state.isSubmitting,
											]}
										>
											{([canSubmit, isSubmitting]) => (
												<Button
													type="submit"
													disabled={
														hcaptchaSiteKey.length === 0 ||
														!canSubmit ||
														!captchaToken ||
														isSubmitting
													}
													className="h-12 w-full rounded-full font-semibold shadow-md dark:shadow-none"
												>
													{isSubmitting ? (
														<span className="flex items-center justify-center gap-2">
															<span
																className="size-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"
																aria-hidden
															/>
															Sending…
														</span>
													) : (
														"Send message"
													)}
												</Button>
											)}
										</form.Subscribe>
									</form>
								</Card>
							</div>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}
